import type { PlacementPosition, ShapedComponent, Surface } from '@repo/api-contracts';
import { createRaster, type Raster } from '@repo/warp-engine';
import { applyShapeMask, rasterizeShapeMask } from '../raster/shape-mask';
import type { PhotoDimensions } from '../types';
import { surfaceMmToPhotoPoint } from './surface-mapping';

export interface ProjectGuideInput {
  /** 안내선 이미지(safeAreaKey). 사진과 크기가 달라도 비율로 표본한다. */
  readonly guide: Raster;
  readonly surface: Surface;
  readonly photo: PhotoDimensions;
  readonly component: ShapedComponent;
  readonly position: PlacementPosition;
  readonly pxPerMm: number;
}

/**
 * 안내선 이미지를 평면 편집 캔버스 좌표로 가져온다(T10). 평면 픽셀마다 mm → 사진 px 로 정변환해 가장 가까운 픽셀을 복사한다.
 * flat 은 평행 변환, cylinder 는 frameToWarped, mesh 는 칸별 양선형 변환. 뒷면·실루엣 밖은 투명이다. 결과는 호출자가 면·축척별로 캐시한다.
 */
export const projectGuideToFlat = ({
  guide,
  surface,
  photo,
  component,
  position,
  pxPerMm,
}: ProjectGuideInput): Raster => {
  const width = Math.max(1, Math.ceil(component.widthMm * pxPerMm));
  const height = Math.max(1, Math.ceil(component.heightMm * pxPerMm));
  const out = createRaster(width, height);
  // 로더가 4MP 초과 사진을 줄이거나 safeAreaSize 가 photoSize 와 다를 수 있어 안내선 크기 비율로 표본 좌표를 맞춘다.
  const scaleX = guide.width / photo.widthPx;
  const scaleY = guide.height / photo.heightPx;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const point = {
        xMm: position.xMm + (x + 0.5) / pxPerMm,
        yMm: position.yMm + (y + 0.5) / pxPerMm,
      };
      const at = surfaceMmToPhotoPoint(surface, photo, point);
      if (at === null) continue;
      const gx = Math.round(at.x * scaleX);
      const gy = Math.round(at.y * scaleY);
      if (gx < 0 || gy < 0 || gx >= guide.width || gy >= guide.height) continue;
      const s = (gy * guide.width + gx) * 4;
      out.data.set(guide.data.subarray(s, s + 4), (y * width + x) * 4);
    }
  }
  const mask = rasterizeShapeMask(component, width, height, pxPerMm);
  if (mask !== null) applyShapeMask(out, mask);
  return out;
};
